/**
 * Teams service — wraps `/api/config_sales_team`,
 * `/api/add_update_team[/:id]`, `/api/delete_team/:id`, and related
 * sales-team config endpoints.
 *
 * Parity target: Angular `sales-teams.component.ts`.
 */

import apiClient from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import type { TeamRecord, TeamSubmitPayload } from '../types/team'

export async function getTeams(): Promise<TeamRecord[]> {
  try {
    const response = await apiClient.get<TeamRecord[]>('/api/config_sales_team')
    return Array.isArray(response.data) ? response.data : []
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load teams')
  }
}

/**
 * Angular `saveSalesTeam` wraps manager ids in single-element arrays.
 */
export async function saveTeam(
  payload: TeamSubmitPayload,
  teamId?: number | string,
): Promise<void> {
  try {
    const url =
      teamId != null && teamId !== ''
        ? `/api/add_update_team/${teamId}`
        : '/api/add_update_team'
    const body = {
      ID: teamId ?? '',
      TeamName: payload.TeamName,
      Relation_Mgr_Id: [payload.Relation_Mgr_Id],
      CustSuccMgr_Id: [payload.CustSuccMgr_Id],
    }
    await apiClient.post(url, body)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to save team')
  }
}

export async function deleteTeam(teamId: number | string): Promise<void> {
  try {
    await apiClient.post(`/api/delete_team/${teamId}`)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to delete team')
  }
}

/**
 * The `/api/config_sales_team` response stores sales_manager /
 * account_manager as arrays but the legacy UI only supports the first entry.
 */
export function firstManagerId(refs?: { user_id: number | string }[]): string {
  if (!refs || refs.length === 0) return ''
  const first = refs[0]
  return first?.user_id == null ? '' : String(first.user_id)
}

import type {
  AssignTeamBody,
  CompanyAssignmentListParams,
  CompanyAssignmentListResponse,
  CompanyCityMap,
  SalesRoleTitleKind,
} from '../types'

export type {
  AssignTeamBody,
  CompanyAssignmentListParams,
  CompanyAssignmentListResponse,
  CompanyAssignmentRow,
  CompanyCityMap,
  SalesRoleTitleKind,
} from '../types'

export async function updateSalesRoleTitle(name: SalesRoleTitleKind, value: string): Promise<void> {
  try {
    await apiClient.post('/api/update_sales_role_title', { name, value })
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to update role title')
  }
}

/** Angular: `GET /company_city` (no `/api` prefix). */
export async function getCompanyCityMap(): Promise<CompanyCityMap> {
  try {
    const res = await apiClient.get<CompanyCityMap>('/company_city')
    return res.data && typeof res.data === 'object' ? res.data : {}
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load cities')
  }
}

export async function getCompanyAssignmentList(
  params: CompanyAssignmentListParams,
): Promise<CompanyAssignmentListResponse> {
  try {
    const res = await apiClient.get<CompanyAssignmentListResponse>('/api/company_assignment_list', {
      params: cleanParams(params as Record<string, unknown>),
    })
    const d = res.data
    return {
      data: Array.isArray(d?.data) ? d.data : [],
      current: d?.current ?? 1,
      last: d?.last ?? 1,
      total: d?.total ?? 0,
    }
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load company assignments')
  }
}

export async function assignTeamToCompanies(body: AssignTeamBody): Promise<void> {
  try {
    await apiClient.post('/api/assign_team', body)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to assign team')
  }
}

export async function updateSalesLead(leadId: string | number): Promise<void> {
  try {
    await apiClient.post(`/api/update_sales_lead/${leadId}`)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to update sales lead')
  }
}

function cleanParams(o: Record<string, unknown>): Record<string, string | number> {
  const out: Record<string, string | number> = {}
  for (const k of Object.keys(o)) {
    const v = o[k]
    if (v === '' || v === null || v === undefined) continue
    if (typeof v === 'string' || typeof v === 'number') out[k] = v
  }
  return out
}
